import { defineStore } from 'pinia'
import { router } from '@/router'
import axios from '../utils/apiClient'
import { User } from '@/models/User'
import { useCustomizerStore } from '@/stores/customizerStore'
import { useCompanyStore } from '@/stores/companyStore'
import { ElNotification } from 'element-plus'
import { useMenuStore } from '@/stores/menuStore'

interface AuthState {
    user: User | null
    returnUrl: string | null
    isLoading: boolean
}

export const useAuthStore = defineStore({
    id: 'auth',
    state: (): AuthState => ({
        user: null,
        returnUrl: null,
        isLoading: false,
    }),
    getters: {
        isAuthenticated: state => !!state.user,
        userRole: state => state.user?.role || null,
    },
    actions: {
        async initialize() {
            this.isLoading = true
            try {
                await this.restoreSession()
            } finally {
                this.isLoading = false
            }
        },

        async restoreSession() {
            const stored = localStorage.getItem('user')
            if (!stored) return

            try {
                const parsed = JSON.parse(stored)
                this.user = User.fromJSON(parsed)
                axios.defaults.headers.common['Authorization'] = `Bearer ${this.user.token}`
                await this.initializeCompanyAndTheme()
            } catch (err) {
                console.error('Session restoration failed:', err)
                this.clearSession()
            }
        },

        async initializeCompanyAndTheme() {
            const companyStore = useCompanyStore()
            const customizer = useCustomizerStore()

            if (this.user?.company || this.user?.companyid) {
                // Only restrict the company list for specific roles
                if (this.user.company && (this.user.role === 'Employee' || this.user.role === 'User')) {
                    companyStore.setListCompany([this.user.company])
                }

                const id = this.user.company?.id || this.user.companyid
                if (id) {
                    await companyStore.setCompanyID(id)
                }

                if (this.user.company?.theme) {
                    customizer.setTheme(this.user.company.theme || 'ORANGE_THEME')
                }
                return
            }

            if (companyStore.companies.length > 0) {
                await companyStore.setCompanyID(companyStore.companyID)
                return
            }

            // Fallback: fetch from configuration
            try {
                const res = await axios.get('configurations')
                if (res.data.company) companyStore.setListCompany(res.data.company)
                if (res.data.theme) customizer.setTheme(res.data.theme)
            } catch (err) {
                console.error('Failed to initialize configuration', err)
            }
        },

        async login(username: string, password: string) {
            this.isLoading = true
            try {
                const response = await axios.post<{ data: User }>('auth/login', { username, password })
                this.user = User.fromJSON(response.data)
                axios.defaults.headers.common['Authorization'] = `Bearer ${this.user.token}`

                // Persist session forever
                localStorage.setItem('user', JSON.stringify(response.data))

                await this.initializeCompanyAndTheme()
                await this.handlePostLogin()
            } catch (err) {
                this.clearSession()
                throw err
            } finally {
                this.isLoading = false
            }
        },

        async handlePostLogin() {
            if (!this.user) return

            if (this.user.isExpired) {
                await router.push('/auth/expired')
                return
            }

            if (this.user.isChangePassword) {
                await router.push('/auth/change-password')
                return
            }
            const redirectPath = this.returnUrl || this.user.homePage || this.getDefaultRoute() || '/'
            this.returnUrl = null
            await router.push(redirectPath)
        },

        getDefaultRoute(): string {
            if (!this.user) return '/auth/login'
            return this.user.homePage
            // switch (this.user.role) {
            //     case 'superAdmin': return '/dashboard'
            //     case 'admin': return '/admin/overview'
            //     default: return '/'
            // }
        },

        clearSession() {
            this.user = null
            delete axios.defaults.headers.common['Authorization']
            localStorage.removeItem('user')
            const menuStore = useMenuStore()
            menuStore.clearMenu()
        },

        async logout(showNotification = true) {
            try {
                await axios.post('auth/Logout', {}, { headers: { Authorization: `Bearer ${this.user?.token}` } })
                this.clearSession()
                this.returnUrl = null
                if (showNotification) {
                    ElNotification.success({
                        title: 'Logged out',
                        message: 'You have been successfully logged out',
                        showClose: false,
                    })
                }
                await router.push('/auth/login')
            } catch (err) {
                console.error('Logout failed:', err)
                this.clearSession()
                if (showNotification) {
                    ElNotification.success({
                        title: 'Logged out',
                        message: 'You have been successfully logged out',
                        showClose: false,
                    })
                }
                await router.push('/auth/login')
            }
        },
    },
})
